import { DeviceRegistryRecordTypedCamera } from '@tryiris-ai/mailroom-helper/deviceRegistry';
import { toIdFromHex, wrapApiCall } from '#~/api/index.js';
import { logger } from '#~/logger/index.js';
import { nats } from '#~/nats/index.js';

/** What the customer typed, and the saved camera it belongs to. */
export type TypedCameraRecord = {
  token: string;
  /** Hex id of the organization that owns the camera's room. */
  organizationId: string | undefined;
  /** Hex id of the saved camera. */
  deviceId: string;
  typedBrand: string | undefined;
  typedModel: string | undefined;
};

/**
 * ENG-4941: tell the device registry which brand/model a customer typed for a camera that
 * is not listed ("isn't listed" / "isn't supported yet" pop-ups). Product ranks these.
 *
 * Fire-and-forget by contract: this NEVER rejects and returns nothing, because the camera
 * is already saved and a failed tally must not touch the save. Callers do not await it.
 * The text is unverified; the server stores it as an org-scoped observation only (it does
 * not add a model, change matching, or appear in anyone's lists), so nothing is applied
 * to the camera here.
 *
 * @param record - The saved camera and the typed text.
 */
export const recordTypedCamera = async (record: TypedCameraRecord): Promise<void> => {
  try {
    const typedBrand = record.typedBrand?.trim() ?? '';
    const typedModel = record.typedModel?.trim() ?? '';
    const organizationId = toIdFromHex(record.organizationId);

    // Nothing typed (a listed camera), or no org to file it under: nothing to record.
    if (!(typedBrand || typedModel) || !organizationId) {
      return;
    }

    await wrapApiCall(async () =>
      DeviceRegistryRecordTypedCamera.request(nats.cloudConnection(), {
        token: record.token,
        organizationId,
        deviceId: toIdFromHex(record.deviceId) ?? new Uint8Array(),
        typedBrand,
        typedModel
      })
    );
  } catch (error) {
    // Log and move on. The camera save already succeeded.
    logger.warning('typed-camera: could not record typed brand/model', {
      error: error instanceof Error ? error.message : String(error)
    });
  }
};
